/**
 * Story images on web. Every story image is stored at a few WebP widths (`STORY_IMAGE_WIDTHS`); these
 * pick between them so each slot downloads the smallest file that fills it sharply.
 *
 * An `<img>` gets the whole set as `srcSet` with a `sizes` hint and lets the browser choose. A slot drawn
 * as a CSS background (the rail's `ImageCard`) can't take a `srcSet`, so it gets the one variant wide
 * enough for its width at this screen's pixel density.
 */
import type { StoryImageAsset } from '@elara/client-core/stories/stories';

import { pickStoryImageVariant } from '@elara/domain/models/schemas/story-schemas';

function pixelRatio(): number {
  return typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
}

/** The URL of the smallest variant that fills `cssWidth` CSS pixels on this screen. */
export function storyImageUrl(asset: StoryImageAsset, cssWidth: number): string {
  return pickStoryImageVariant(asset, cssWidth * pixelRatio()).url;
}

/**
 * `src` / `srcSet` / `sizes` for an `<img>` (or `AppImage`) showing a story image. `sizes` is the slot's
 * width as the browser should assume it, e.g. `'(min-width: 1024px) 1024px, 100vw'`. `src` is the
 * fallback for a browser that ignores `srcSet`: the middle width, which is fine everywhere.
 */
export function storyImageProps(asset: StoryImageAsset, sizes: string): { src: string; srcSet: string; sizes: string } {
  return {
    src: pickStoryImageVariant(asset, 1280).url,
    srcSet: asset.variants.map((variant) => `${variant.url} ${variant.width}w`).join(', '),
    sizes,
  };
}
